import { useState } from "react";
import {
  Package,
  MapPin,
  Truck,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react";
import "./TrackOrder.css";

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [trackingId, setTrackingId] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const API_BASE = "https://backend-wellshape.vercel.app/order";

  // Simple stage order
  const STAGES = [
    "paid",
    "processing",
    "ready_for_labelling",
    "ready_for_handover",
    "pickup_pending",
    "pickup_done",
    "out_for_delivery",
    "delivered",
  ];

  const STAGE_LABELS = {
    paid: "Order Placed",
    processing: "Processing",
    on_hold: "On Hold",
    invalid: "Invalid",
    ready_for_labelling: "Ready for Labelling",
    ready_for_handover: "Ready for Handover",
    pickup_pending: "Pickup Pending",
    pickup_done: "Picked Up",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    canceled_orders: "Cancelled",
  };

  const handleTrackByOrder = async () => {
    setError("");
    if (!orderNumber.trim() || !email.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: orderNumber, email }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Order not found");

      setOrderData(data.order);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTrackByTrackingId = async () => {
    setError("");
    if (!trackingId.trim()) {
      setError("Please enter tracking ID");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/track/${trackingId}`);

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Tracking ID not found");
      console.log(data);
      setOrderData(data.order);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setOrderNumber("");
    setEmail("");
    setTrackingId("");
    setOrderData(null);
    setError("");
  };

  const getCurrentStageIndex = (status) => {
    return STAGES.indexOf(status);
  };

  const getStatusIcon = (isCompleted) => {
    if (isCompleted) {
      return <CheckCircle size={20} />;
    }
    return <Clock size={20} />;
  };

  const getStatusColor = (status) => {
    if (status === "delivered") return "text-green-600";
    if (status === "canceled_orders") return "text-red-600";
    if (status === "on_hold" || status === "invalid") return "text-yellow-600";
    return "text-blue-600";
  };

  return (
    <div className="track-order-container">
      <div className="track-order-wrapper">
        <div className="track-order-header">
          <h1>Track Your Order</h1>
          <p>Enter your order details to track your package</p>
        </div>

        {!orderData ? (
          <div className="track-order-card">
            {error && (
              <div className="error-message">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            <div className="track-order-grid">
              {/* Left Side - Order Number Method */}
              <div className="track-method">
                <div className="input-group">
                  <label>Order Number</label>
                  <input
                    type="text"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    placeholder="e.g., ORD-123456"
                  />
                </div>

                <div className="input-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                  />
                </div>

                <button
                  onClick={handleTrackByOrder}
                  disabled={loading}
                  className="track-btn"
                >
                  {loading ? "TRACKING..." : "TRACK"}
                </button>
              </div>

              {/* Divider */}
              <div className="divider-wrapper">
                <div className="divider-line"></div>
                <span className="divider-text">OR</span>
              </div>

              {/* Right Side - Tracking ID Method */}
              <div className="track-method">
                <div className="input-group">
                  <label>Tracking ID</label>
                  <input
                    type="text"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    placeholder="e.g., TRK123456789"
                  />
                </div>

                <button
                  onClick={handleTrackByTrackingId}
                  disabled={loading}
                  className="track-btn track-btn-offset"
                >
                  {loading ? "TRACKING..." : "TRACK"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="order-status-container">
            <div className="order-status-card">
              <div className="status-header">
                <div>
                  <h2>
                    Order Status:{" "}
                    <span
                      className={`status-text ${getStatusColor(orderData.status)}`}
                    >
                      {STAGE_LABELS[orderData.status] || orderData.status}
                    </span>
                  </h2>
                  <p className="order-id">Order #{orderData.orderId}</p>
                  {orderData.trackingId && (
                    <p className="tracking-id">
                      Tracking ID: {orderData.trackingId}
                    </p>
                  )}
                </div>
                <button onClick={resetForm} className="track-another-btn">
                  Track Another
                </button>
              </div>

              <div className="info-grid">
                <div className="info-card info-card-blue">
                  <MapPin size={20} />
                  <div>
                    <p className="info-label">Current Location</p>
                    <p className="info-value">
                      {orderData.tracking?.currentLocation || "Processing"}
                    </p>
                  </div>
                </div>
                <div className="info-card info-card-green">
                  <Clock size={20} />
                  <div>
                    <p className="info-label">Estimated Delivery</p>
                    <p className="info-value">
                      {orderData.tracking?.estimatedDelivery ||
                        "Calculating..."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Progress Steps - Only for normal flow */}
              {!["canceled_orders", "on_hold", "invalid"].includes(
                orderData.status,
              ) && (
                <div className="progress-steps">
                  {orderData.tracking?.steps?.map((step, index) => {
                    const currentIndex = getCurrentStageIndex(orderData.status);
                    const stepIndex = getCurrentStageIndex(step.status);
                    const isCompleted = stepIndex <= currentIndex;

                    return (
                      <div key={index} className="progress-step">
                        <div className="step-icon-wrapper">
                          <div
                            className={`step-icon ${isCompleted ? "completed" : "pending"}`}
                          >
                            {getStatusIcon(isCompleted)}
                          </div>
                          {index < orderData.tracking.steps.length - 1 && (
                            <div
                              className={`step-line ${isCompleted ? "completed" : "pending"}`}
                            ></div>
                          )}
                        </div>
                        <div className="step-content">
                          <h3 className={isCompleted ? "completed" : "pending"}>
                            {step.status}
                          </h3>
                          <p className={isCompleted ? "completed" : "pending"}>
                            {step.date}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Special status message */}
              {["canceled_orders", "on_hold", "invalid"].includes(
                orderData.status,
              ) && (
                <div className="special-status-message">
                  <AlertCircle size={48} className="text-yellow-500" />
                  <h3>{STAGE_LABELS[orderData.status]}</h3>
                  <p>
                    {orderData.status === "canceled_orders" &&
                      "This order has been cancelled."}
                    {orderData.status === "on_hold" &&
                      "This order is currently on hold. Please contact support."}
                    {orderData.status === "invalid" &&
                      "This order is marked as invalid. Please contact support."}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
